import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { anfragenApi, type GetAnfragenParams } from '@/services/api/anfragen';
import type {
  CreateAnfragePayload,
  PositionAnfragenMap,
  QuestionAnswersResponse,
  UpdateAnfrageItemPayload,
} from '@/types/anfragen';

export const useAnfragen = (params?: GetAnfragenParams) => {
  return useQuery({
    queryKey: ['anfragen', params],
    queryFn: () => anfragenApi.getAll(params),
  });
};

export const useAnfrage = (uid: string) => {
  return useQuery({
    queryKey: ['anfrage', uid],
    queryFn: () => anfragenApi.getByUid(uid),
    enabled: !!uid,
  });
};

export const useCreateAnfrage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAnfragePayload) => anfragenApi.create(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['anfragen'] });
      queryClient.invalidateQueries({
        queryKey: ['anfragen-by-delivery-group', variables.delivery_group_id],
      });
      queryClient.invalidateQueries({ queryKey: ['workflow-execution-log'] });
    },
  });
};

export const useUpdateAnfrageStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uid, statusId, integrationId }: { uid: string; statusId: number; integrationId?: number }) =>
      anfragenApi.updateStatus(uid, statusId, integrationId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['anfrage', variables.uid] });
      queryClient.invalidateQueries({ queryKey: ['anfragen'] });
      queryClient.invalidateQueries({ queryKey: ['workflow-execution-log'] });
    },
  });
};

export const useRejectAnfrage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uid, rejectionReason }: { uid: string; rejectionReason: string }) =>
      anfragenApi.reject(uid, rejectionReason),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['anfrage', variables.uid] });
      queryClient.invalidateQueries({ queryKey: ['anfragen'] });
      queryClient.invalidateQueries({ queryKey: ['return-notes'] });
      queryClient.invalidateQueries({ queryKey: ['workflow-execution-log'] });
    },
  });
};

export const useUpdateAnfrageLanguage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uid, language }: { uid: string; language: string }) =>
      anfragenApi.updateLanguage(uid, language),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['anfrage', variables.uid] });
      queryClient.invalidateQueries({ queryKey: ['anfragen'] });
    },
  });
};

export const useDeleteAnfrage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uid: string) => anfragenApi.delete(uid),
    onSuccess: (_data, uid) => {
      queryClient.invalidateQueries({ queryKey: ['anfragen'] });
      queryClient.invalidateQueries({ queryKey: ['anfrage', uid] });
      queryClient.invalidateQueries({ queryKey: ['anfragen-by-delivery-group'] });
    },
  });
};

export const useReturnCountsByStatus = () => {
  return useQuery({
    queryKey: ['anfragen', 'counts-by-status'],
    queryFn: () => anfragenApi.countsByStatus(),
    staleTime: 60_000, // 1 min cache
    refetchInterval: 60_000,
  });
};

export const useUpdateAnfrageItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      uid,
      itemId,
      payload,
    }: {
      uid: string;
      itemId: number;
      payload: UpdateAnfrageItemPayload;
    }) => anfragenApi.updateItem(uid, itemId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['anfrage', variables.uid] });
    },
  });
};

export const useAnfragenByDeliveryGroup = (uid: string | undefined) => {
  return useQuery<PositionAnfragenMap>({
    queryKey: ['anfragen-by-delivery-group', uid],
    queryFn: () => anfragenApi.getByDeliveryGroup(uid ?? ''),
    enabled: !!uid,
  });
};

export const useAnfrageQuestionAnswers = (uid: string | undefined) => {
  return useQuery<QuestionAnswersResponse>({
    queryKey: ['anfrage-question-answers', uid],
    queryFn: () => anfragenApi.getQuestionAnswers(uid ?? ''),
    enabled: !!uid,
  });
};

export const useReturnsTrend = (days = 30) => {
  return useQuery({
    queryKey: ['anfragen', 'trend', days],
    queryFn: () => anfragenApi.trend(days),
    staleTime: 5 * 60_000,
  });
};

export const useTopReturnReasons = (limit = 5) => {
  return useQuery({
    queryKey: ['anfragen', 'top-reasons', limit],
    queryFn: () => anfragenApi.topReasons(limit),
    staleTime: 5 * 60_000,
  });
};
